import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'

export const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return screenSize
}

const useEventListener = (event, listener, useCapture) => {
  useEffect(() => {
    if (listener) {
      listener()
      window.addEventListener(event, listener, useCapture)

      return () => window.removeEventListener(event, listener, useCapture)
    }

    return () => {}
  }, [event, listener, useCapture])
}

export const useDocumentSize = () => {
  const [documentSize, setDocumentSize] = useState({
    documentWidth: document.documentElement.scrollWidth,
    documentHeight: document.documentElement.scrollHeight,
  })

  useEffect(() => {
    const handleResize = () => {
      setDocumentSize({
        documentWidth: document.documentElement.scrollWidth,
        documentHeight: document.documentElement.scrollHeight,
      })
    }

    window.addEventListener('resize', handleResize)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return documentSize
}

export function useDocumentTitle(title, prevailOnUnmount = false) {
  const defaultTitle = useRef(document.title)

  useEffect(() => {
    document.title = title
  }, [title])

  useEffect(
    () => () => {
      if (!prevailOnUnmount) {
        document.title = defaultTitle.current
      }
    },
    [],
  )
}

export const useScrollTo = ({ offset = 0 }) => {
  const ref = useRef(null)
  const [shouldScrollTo, setShouldScrollTo] = useState(false)

  useEffect(() => {
    if (ref.current && shouldScrollTo) {
      const barHeight = `${137 + offset}px`
      ref.current.style.scrollMargin = barHeight
      ref.current?.scrollIntoView({ behavior: 'smooth' })
      setShouldScrollTo(false)
    }
  }, [shouldScrollTo, offset])

  return [ref, setShouldScrollTo]
}

export const useScrollToWithParentRef = ({ offset = 0, parentRef }) => {
  const ref = useRef(null)
  const [shouldScrollTo, setShouldScrollTo] = useState(false)

  useEffect(() => {
    const scrollElement = parentRef ? parentRef.current : window

    if (ref.current && shouldScrollTo) {
      const barHeight = `${137 + offset}px`
      ref.current.style.scrollMargin = barHeight
      scrollElement.scrollTo({
        top: ref.current.offsetTop,
        behavior: 'smooth',
      })
      setShouldScrollTo(false)
    }
  }, [shouldScrollTo, offset, parentRef])

  return [ref, setShouldScrollTo]
}

export const useNavigateWithParams = () => {
  const navigate = useNavigate()
  const [urlSearchParams, setUrlSearchParams] = useSearchParams()

  return (url, params) => {
    const searchParams = createSearchParams(params).toString()
    const urlsrprm = urlSearchParams.toString()

    navigate(url + '?' + urlsrprm)
  }
}

const useElementDimensions = (props) => {
  const ref = useRef(null)
  const [dimensions, setDimensions] = useState(null)
  let finalProps = props || { updateOnScroll: true }

  const refresh = useCallback(() => {
    const domRect = ref.current?.getBoundingClientRect()
    if (domRect) {
      setDimensions((prev) => {
        if (
          prev &&
          prev.width === domRect.width &&
          prev.height === domRect.height &&
          prev.top === domRect.top &&
          prev.left === domRect.left &&
          prev.right === domRect.right &&
          prev.bottom === domRect.bottom
        ) {
          return prev
        }
        return domRect
      })
    } else {
      setDimensions(null)
    }
  }, [])

  useEventListener('resize', refresh)
  useEventListener('scroll', finalProps?.updateOnScroll ? refresh : () => {}, true)

  return { dimensions, ref, refresh }
}

function parseStoredJson(value) {
  try {
    return value ? JSON.parse(value) : null
  } catch (error) {
    console.error('Error parsing localStorage data:', error)
    return null
  }
}

function getActivePeekInFromStoredData(peekData) {
  if (!peekData) {
    return null
  }

  if (!Array.isArray(peekData)) {
    return peekData?.id ? peekData : null
  }

  const now = Date.now()

  return (
    peekData.find((peekIn) => {
      if (!peekIn?.id) {
        return false
      }

      if (!peekIn.expiresAt) {
        return true
      }

      const expiresAt = Date.parse(peekIn.expiresAt)
      return Number.isFinite(expiresAt) && expiresAt > now
    }) || null
  )
}

export function useHasScrolled() {
  const [hasScrolled, setHasScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 0)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initialize state on mount

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return hasScrolled
}
// export const useIdleScreen = ({ idleTime = 10000, manualReset = false }) => {
//   const [lastActiveTime, setLastActiveTime] = useState(dayjs())
//   const [isIdle, setIsIdle] = useState(false)

//   useEffect(() => {
//     const updateLastActiveTime = () => {
//       if (!manualReset || !isIdle) {
//         setLastActiveTime(dayjs()) // Reset on user interaction
//       }
//     }

//     const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
//     events.forEach((event) => window.addEventListener(event, updateLastActiveTime))

//     return () => {
//       events.forEach((event) => window.removeEventListener(event, updateLastActiveTime))
//     }
//   }, [manualReset])

//   useEffect(() => {
//     const interval = setInterval(() => {
//       setIsIdle(dayjs().diff(lastActiveTime, 'milliseconds') >= idleTime)
//     }, 500) // Check every 500ms

//     return () => clearInterval(interval)
//   }, [lastActiveTime, idleTime])

//   return {
//     isIdle,
//     resetIdle: () => {
//       setLastActiveTime(dayjs())
//       setIsIdle(false)
//     }, // Manual reset if needed
//   }
// }

export const useIdleScreen = ({ idleTime = 10000, manualReset = false }) => {
  const [isIdle, setIsIdle] = useState(false)

  useEffect(() => {
    let timer

    const triggerIdle = () => {
      setIsIdle(true)
    }

    const resetTimer = () => {
      if (!manualReset || !isIdle) {
        clearTimeout(timer)
        setIsIdle(false)
        timer = setTimeout(triggerIdle, idleTime)
      }
    }

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
    events.forEach((event) => window.addEventListener(event, resetTimer))

    resetTimer()

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer))
      clearTimeout(timer)
    }
  }, [idleTime, manualReset, isIdle])

  return { isIdle, resetIdle: () => setIsIdle(false) }
}

export function useIsMentorCheckedInOrPeekedInValidity({ byPassMentorIdCheck = false }) {
  const [checkedInDetails, setCheckedInDetails] = useState(null)
  const [roomDataStringified, setRoomDataStringified] = useState(null)
  const [dataFetchedOnce, setDataFetchedOnce] = useState(false)

  useEffect(() => {
    const refreshStoredAccess = () => {
      const newCheckIn = localStorage.getItem('checkedInDetails')
      const newPeek = localStorage.getItem('peekedInRoomDetails')

      setCheckedInDetails((prev) => (prev !== newCheckIn ? newCheckIn : prev))
      setRoomDataStringified((prev) => (prev !== newPeek ? newPeek : prev))
      setDataFetchedOnce(true)
    }

    refreshStoredAccess()

    const interval = setInterval(refreshStoredAccess, 1000) // runs every second
    window.addEventListener('storage', refreshStoredAccess)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', refreshStoredAccess)
    }
  }, [])

  const parsedItem = useMemo(
    () => parseStoredJson(checkedInDetails),
    [checkedInDetails],
  )
  const parsedPeekData = useMemo(
    () => parseStoredJson(roomDataStringified),
    [roomDataStringified],
  )
  const activePeekIn = useMemo(
    () => getActivePeekInFromStoredData(parsedPeekData),
    [parsedPeekData],
  )

  let finalResult = { isCheckedIn: true, isPeekedIn: true }

  if (!dataFetchedOnce) {
    return { isCheckedIn: true, isPeekedIn: true }
  }

  if (!parsedItem) {
    finalResult.isCheckedIn = false
  }
  if (!parsedItem?.chamberRoomId) {
    finalResult.isCheckedIn = false
  }

  if (!activePeekIn) {
    finalResult.isPeekedIn = false
  }
  if (!activePeekIn?.id) {
    finalResult.isPeekedIn = false
  }
  return finalResult
}

export function useCheckedInOrPeekedInChamberRoomId() {
  const [roomId, setRoomId] = useState(null)

  useEffect(() => {
    const getRoomId = () => {
      try {
        const peekedInRoomDetails = localStorage.getItem('peekedInRoomDetails')
        const checkedInDetails = localStorage.getItem('checkedInDetails')

        if (peekedInRoomDetails) {
          const parsedPeekedInRoomDetails = parseStoredJson(peekedInRoomDetails)
          const activePeekIn = getActivePeekInFromStoredData(parsedPeekedInRoomDetails)
          if (activePeekIn?.id) {
            setRoomId(activePeekIn.id)
            return
          }
        }

        if (checkedInDetails) {
          const parsedCheckedInDetails = parseStoredJson(checkedInDetails)
          setRoomId(parsedCheckedInDetails?.chamberRoomId)
        } else {
          setRoomId(null)
        }
      } catch (error) {
        console.error('Error parsing localStorage data:', error)
        setRoomId(null)
      }
    }

    // Initial fetch
    getRoomId()

    // Listen for changes in localStorage
    const handleStorageChange = () => {
      getRoomId()
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return roomId
}

export function useCheckedInOnlyChamberRoomId() {
  const [roomId, setRoomId] = useState(null)

  useEffect(() => {
    const getRoomId = () => {
      try {
        const checkedInDetails = localStorage.getItem('checkedInDetails')

        if (checkedInDetails) {
          const parsedCheckedInDetails = JSON.parse(checkedInDetails)
          setRoomId(parsedCheckedInDetails?.chamberRoomId)
        } else {
          setRoomId(null)
        }
      } catch (error) {
        console.error('Error parsing localStorage data:', error)
        setRoomId(null)
      }
    }

    // Initial fetch
    getRoomId()

    // Listen for changes in localStorage
    const handleStorageChange = () => {
      getRoomId()
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return roomId
}

export default useElementDimensions

/**
 * Custom hook for auto-saving functionality
 * @param {Function} saveFunction - Function to call when auto-saving
 * @param {Array} dependencies - Array of state values to watch for changes
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay in milliseconds before auto-saving (default: 1500)
 * @param {boolean} options.enabled - Whether auto-save is enabled (default: true)
 * @param {boolean} options.immediate - Whether to save immediately on first change (default: false)
 * @returns {Object} - Object containing auto-save state and control functions
 */
export const useAutoSave = (saveFunction, dependencies = [], options = {}) => {
  const {
    delay = 1500,
    enabled = true,
    immediate = false,
  } = options

  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState({ text: '', status: 'idle' })
  const [userEditing, setUserEditing] = useState(false)
  
  const lastSavedState = useRef({})
  const timeoutRef = useRef(null)
  const hasMounted = useRef(false)

  // Initialize last saved state
  useEffect(() => {
    if (!hasMounted.current) {
      lastSavedState.current = JSON.stringify(dependencies)
      hasMounted.current = true
    }
  }, [])

  // Function to check if state has changed
  const hasStateChanged = useCallback(() => {
    const currentState = JSON.stringify(dependencies)
    return lastSavedState.current !== currentState
  }, [dependencies])

  // Auto-save function with status management
  const performAutoSave = useCallback(async () => {
    if (!enabled || !saveFunction || !userEditing) return

    setIsAutoSaving(true)
    setAutoSaveStatus({ text: 'Saving', status: 'saving' })

    try {
      await saveFunction()
      lastSavedState.current = JSON.stringify(dependencies)
      setAutoSaveStatus({ text: 'Saved', status: 'saved' })
      setUserEditing(false)
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setAutoSaveStatus({ text: '', status: 'idle' })
      }, 3000)
    } catch (error) {
      setAutoSaveStatus({ text: 'Save Failed', status: 'error' })
      console.error('Auto-save failed:', error)
    } finally {
      setIsAutoSaving(false)
    }
  }, [enabled, saveFunction, userEditing, dependencies])

  // Trigger auto-save on dependency changes
  useEffect(() => {
    if (!enabled || !hasMounted.current) return

    if (userEditing && hasStateChanged()) {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (immediate) {
        performAutoSave()
      } else {
        // Set new timeout
        timeoutRef.current = setTimeout(() => {
          performAutoSave()
        }, delay)
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, userEditing, hasStateChanged, performAutoSave, delay, immediate])

  // Manual trigger for user editing state
  const triggerUserEdit = useCallback(() => {
    setUserEditing(true)
  }, [])

  // Manual save function
  const saveNow = useCallback(async () => {
    if (!saveFunction) return
    await performAutoSave()
  }, [performAutoSave])

  // Reset auto-save state
  const resetAutoSave = useCallback(() => {
    setUserEditing(false)
    setAutoSaveStatus({ text: '', status: 'idle' })
    lastSavedState.current = JSON.stringify(dependencies)
  }, [dependencies])

  return {
    isAutoSaving,
    autoSaveStatus,
    userEditing,
    triggerUserEdit,
    saveNow,
    resetAutoSave,
    hasUnsavedChanges: hasStateChanged(),
  }
}

/**
 * Higher-order function that wraps state setters to automatically trigger user editing
 * @param {Function} setter - State setter function
 * @param {Function} triggerUserEdit - Function to trigger user editing state
 * @returns {Function} - Wrapped setter function
 */
export const createAutoSaveSetter = (setter, triggerUserEdit) => {
  return (value) => {
    triggerUserEdit()
    setter(value)
  }
}

/**
 * Hook to create auto-save wrapped setters for multiple state variables
 * @param {Object} setters - Object containing state setter functions
 * @param {Function} triggerUserEdit - Function to trigger user editing state
 * @returns {Object} - Object containing wrapped setter functions
 */
export const useAutoSaveSetters = (setters, triggerUserEdit) => {
  const wrappedSetters = {}
  
  Object.keys(setters).forEach(key => {
    wrappedSetters[key] = createAutoSaveSetter(setters[key], triggerUserEdit)
  })
  
  return wrappedSetters
}
